import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type SellerRecallRow } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from '@/features/admin/AdminError';

/**
 * Recalls that reached this pharmacy's own shelf.
 *
 * ── WHY THE PHARMACY HAS TO CONFIRM BY HAND ─────────────────────────────────
 *
 * ChiCare takes the stock off sale automatically the moment a recall is
 * issued, so nothing more can be bought. But software cannot take a box off a
 * physical shelf, and the packs that matter most are the ones already in a
 * dispensing drawer, a delivery bag or a fridge.
 *
 * So somebody here looks, and says what they found. Until they do, the recall
 * stays open and this pharmacy stays on the chase list — because a recall that
 * was broadcast and never acknowledged is a recall that did not happen, and
 * "we sent a message" is not a defence anybody wants to offer a regulator.
 *
 * ── AND WHY IT IS NOT A SCOLDING ────────────────────────────────────────────
 *
 * A bad batch is almost never the pharmacy's doing — it came from a
 * manufacturer, through a distributor, in a sealed box. The tone is urgent
 * about the medicine and neutral about the seller.
 */
export function SellerRecallsScreen() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['seller', 'recalls'],
    queryFn: ({ signal }) => api.sellerRecalls(signal),
    refetchInterval: 120_000,
  });

  if (isError) {
    return (
      <AdminError title="We couldn't load your recalls" error={error} onRetry={() => refetch()} />
    );
  }

  const recalls = data?.recalls ?? [];

  return (
    <div className="mx-auto w-full max-w-[860px] px-4 py-6 xl:px-8">
      <header className="mb-5">
        <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Recalls</h1>
        <p className="m-0 mt-1 max-w-[62ch] text-[13px] leading-relaxed text-muted">
          Batches pulled from sale. We take them off your listings automatically — what we cannot do
          is check your shelf, so each one needs you to look and tell us what you found.
        </p>
      </header>

      {data && data.outstanding > 0 ? (
        <div
          data-testid="recalls-outstanding"
          className="mb-4 flex items-start gap-2.5 rounded-card border border-danger bg-danger-tint px-4 py-3"
        >
          <Icon name="alert" size={16} strokeWidth={2} className="mt-px shrink-0 text-danger" />
          <p className="m-0 text-[13px] leading-snug text-danger">
            <b>{data.outstanding}</b> recall{data.outstanding === 1 ? '' : 's'} still need
            {data.outstanding === 1 ? 's' : ''} checking. Please look at the shelf today.
          </p>
        </div>
      ) : null}

      {isLoading ? (
        <div className="space-y-2.5">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-[140px] w-full" radius="card" />
          ))}
        </div>
      ) : recalls.length === 0 ? (
        <EmptyState
          title="Nothing has been recalled"
          body="If a batch you hold is ever pulled, it appears here and comes off your listings straight away."
          action={undefined}
        />
      ) : (
        <div className="space-y-2.5">
          {recalls.map((one) => (
            <RecallCard key={one.reference ?? ''} recall={one} />
          ))}
        </div>
      )}
    </div>
  );
}

function RecallCard({ recall }: { recall: SellerRecallRow }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const critical = recall.severity === 'critical';

  const confirm = useMutation({
    mutationFn: () => api.acknowledgeRecall(recall.reference ?? '', note.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['seller', 'recalls'] });
      setNote('');
    },
  });

  const error = confirm.error instanceof ApiError ? confirm.error : null;

  return (
    <div
      data-testid={`recall-${recall.reference}`}
      className={cn(
        'rounded-card border bg-white p-4',
        recall.needs_confirmation ? 'border-danger' : 'border-line',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="m-0 text-[14.5px] font-bold text-ink">{recall.product}</p>
          <p className="m-0 mt-0.5 font-mono text-[11.5px] text-muted">
            batch {recall.batch_no} · {recall.reference}
          </p>
        </div>

        <span
          className={cn(
            'shrink-0 rounded-pill px-2.5 py-1 text-[11px] font-bold',
            critical ? 'bg-danger-tint text-danger' : 'bg-caution-tint text-caution-ink',
          )}
        >
          {recall.severity_label}
        </span>
      </div>

      <p className="m-0 mt-2.5 text-[13px] leading-relaxed text-ink">{recall.reason}</p>
      {recall.source ? (
        <p className="m-0 mt-1 text-[11.5px] text-faint">Source: {recall.source}</p>
      ) : null}

      {/* What it cost THEM, specifically — never another pharmacy's figures. */}
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 border-t border-line pt-2.5">
        <span className="text-[12px] text-muted">
          <b className="font-mono text-[13px] text-ink">{recall.units_on_shelf}</b> taken off your
          shelf
        </span>
        <span className="text-[12px] text-muted">
          <b className="font-mono text-[13px] text-ink">{recall.units_sold}</b> already sold
        </span>
      </div>

      {recall.needs_confirmation ? (
        <div className="mt-3.5 border-t border-line pt-3.5">
          <label className="block">
            <span className="mb-1.5 block text-[12.5px] font-semibold text-ink">
              What did you find when you checked?
            </span>
            <textarea
              value={note}
              rows={2}
              data-testid={`recall-note-${recall.reference}`}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Checked the shelf, the drawer and the fridge. Found two packs, quarantined them."
              className="w-full rounded-inp border border-line bg-white px-3 py-2 text-[13px] leading-relaxed text-ink outline-none focus:border-blue"
            />
          </label>

          {error ? (
            <p role="alert" className="m-0 mt-1.5 text-[12.5px] font-medium text-danger">
              {error.fieldErrors.note?.[0] ?? error.message}
            </p>
          ) : null}

          <Button
            size="sm"
            className="mt-2.5"
            disabled={note.trim().length < 5 || confirm.isPending}
            onClick={() => confirm.mutate()}
          >
            {confirm.isPending ? 'Recording…' : 'My shelf is clear'}
          </Button>
        </div>
      ) : (
        <div className="mt-3 flex items-start gap-2 border-t border-line pt-2.5">
          <Icon name="shield" size={14} filled className="mt-px shrink-0 text-verified" />
          <p className="m-0 text-[12px] leading-snug text-muted">
            You confirmed this on{' '}
            {recall.acknowledged_at
              ? new Date(recall.acknowledged_at).toLocaleDateString('en-NG')
              : ''}
            {recall.acknowledgement_note ? ` — “${recall.acknowledgement_note}”` : ''}
          </p>
        </div>
      )}
    </div>
  );
}
